import { Observable, of } from 'rxjs';
import { share, switchMap, tap } from 'rxjs/operators';
import { PerformanceProfileRecord as PerformanceProfileRecord } from './performance-profile-record';

export class PerformanceProfileDatabase {
    private static databaseName = 'MsftSmePerformance';
    private static objectStoreName = 'Performance';
    private static version = 1;

    private database: IDBDatabase;
    private databaseShared: Observable<IDBDatabase>;
    private lastError: any;

    public write(record: PerformanceProfileRecord): Observable<void> {
        return this.getDatabase()
            .pipe(switchMap(database => this.storeData(database, record)));
    }

    public read(from?: number): Observable<PerformanceProfileRecord> {
        return this.getDatabase()
            .pipe(switchMap(database => this.restoreData(database, from)));
    }

    public delete(primaryKeys: number[]): Observable<void> {
        return this.getDatabase()
            .pipe(switchMap(database => this.deleteData(database, primaryKeys)));
    }

    public close(): void {
        if (!this.database) {
            return;
        }

        this.database.close();
        this.database = null;
        this.databaseShared = null;
    }

    public deleteAll(): Observable<void> {
        return this.getDatabase()
            .pipe(
                tap(() => this.close()),
                switchMap(() => this.deleteDatabase())
            );
    }

    private deleteDatabase(): Observable<void> {
        return new Observable(observer => {
            const request = window.indexedDB.deleteDatabase(PerformanceProfileDatabase.databaseName);
            request.onerror = () => {
                observer.error(request.error);
            };
            request.onsuccess = () => {
                this.database = null;
                this.databaseShared = null;
                observer.next(null);
                observer.complete();
            };
        });
    }

    private getDatabase(): Observable<IDBDatabase> {
        if (this.database) {
            return of(this.database);
        }

        if (this.databaseShared) {
            return this.databaseShared;
        }

        this.databaseShared = new Observable<any>(observer => {
            const openRequest = window.indexedDB.open(PerformanceProfileDatabase.databaseName, PerformanceProfileDatabase.version);
            openRequest.onerror = () => {
                this.lastError = openRequest.error;
                observer.error(this.lastError);
                openRequest.onerror = null;
            };
            openRequest.onsuccess = () => {
                this.database = openRequest.result;
                observer.next(this.database);
                observer.complete();
            };
            openRequest.onupgradeneeded = event => {
                const database = openRequest.result;
                database.onerror = event2 => {
                    this.lastError = event2;
                    observer.error(this.lastError);
                };
                if (event.oldVersion !== 0 && event.oldVersion < PerformanceProfileDatabase.version) {
                    database.deleteObjectStore(PerformanceProfileDatabase.objectStoreName);
                }

                database.createObjectStore(PerformanceProfileDatabase.objectStoreName, { autoIncrement: true });
            };
        })
        .pipe(share());

        return this.databaseShared;
    }

    private storeData(database: IDBDatabase, record: PerformanceProfileRecord): Observable<void> {
        return new Observable(observer => {
            const transaction = database.transaction([PerformanceProfileDatabase.objectStoreName], 'readwrite');
            transaction.oncomplete = () => {
                observer.complete();
            };
            transaction.onerror = () => {
                observer.error(transaction.error);
            };

            const objectStore = transaction.objectStore(PerformanceProfileDatabase.objectStoreName);
            objectStore.add(record);
        });
    }

    private restoreData(database: IDBDatabase, from?: number): Observable<PerformanceProfileRecord> {
        return new Observable(observer => {
            const transaction = database.transaction([PerformanceProfileDatabase.objectStoreName], 'readonly');
            const objectStore = transaction.objectStore(PerformanceProfileDatabase.objectStoreName);
            let cursor: IDBRequest<IDBCursorWithValue>;
            if (from) {
                const query = IDBKeyRange.lowerBound(from, true);
                cursor = objectStore.openCursor(query, 'next');

            } else {
                cursor = objectStore.openCursor();
            }
            cursor.onsuccess = event => {
                const current: IDBCursorWithValue = (<any>event.target).result;
                if (current && current.value) {
                    const record: PerformanceProfileRecord = current.value;
                    record.primaryKey = <number>current.primaryKey;
                    observer.next(record);
                    current.continue();
                } else {
                    observer.complete();
                }
            };
            cursor.onerror = () => {
                observer.error(cursor.error);
            };
        });
    }

    private deleteData(database: IDBDatabase, primaryKeys: number[]): Observable<void> {
        return new Observable(observer => {
            const transaction = database.transaction([PerformanceProfileDatabase.objectStoreName], 'readwrite');
            transaction.oncomplete = () => {
                observer.complete();
            };
            transaction.onerror = () => {
                observer.error(transaction.error);
            };

            const objectStore = transaction.objectStore(PerformanceProfileDatabase.objectStoreName);
            for (const primaryKey of primaryKeys) {
                objectStore.delete(primaryKey);
            }
        });
    }
}
